/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import React, { useMemo } from "react";
import "../../Styles/styles-ResultsTable.css";
import "../../Styles/styles-Example.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
	MAX_VELO_METRICS,
	TOTAL_DISTANCE_METRICS,
  getMetricsToRender
} from "../../Utils/chart_utils";
import BarChartHighcharts from "../Charts/BarChart.jsx";
import { mapDataForHighcharts, mapDataForMaxVeloChart } from "../../mappers/model/BarChartMapper.js";
import { stackedBarChartDtoToModel } from "../../mappers/model/StackedBarChartMapper.js"
import { mapDataForBarAndBubbleChart } from "../../mappers/model/BarAndBubble.js";
import { mapDataForHighchartsGroupedBar } from "../../mappers/model/GroupedBarChartMapper.js";
import { useSelector } from "react-redux";

function round(num) {
  return Math.round((num + Number.EPSILON) * 10) / 10;
}

export default function BreakdownCharts({
  data,
  hideDistanceZones,
  team_colors,
  dataColorUtil = undefined
}) {
  const {
    info: { metric: teamMetric, display_name: teamName, custom_charts: customChartData, acwr_threshold_config: acwrThresholdConfig
    },
    games
  } = useSelector(({ team }) => team);

  let chart_data = {};


  //for loop through data.metrics
  for (let i = 0; i < data.metrics.length + data.wellness_metrics.length; i++) {
    let metric;
    if (i < data["metrics"].length) {
      metric = data["metrics"][i];
    } else {
      metric = data["wellness_metrics"][i - data["metrics"].length];
    }

    let wellness = metric["wellness"];
    let key = metric["key"];
    chart_data[key] = data[key].map((training, i) => {
      if (key == "distance" && !hideDistanceZones) {
        return {
          date: training.date.slice(5),
          name: training.name,
          player_id: training.player_id,
          disp_name:
            training.name !== undefined
              ? training.name.split(" ")[training.name.split(" ").length - 1]
              : undefined,
          distance: round(training.distance),
          distance_1: Math.round(100 * training.distance_1) / 100,
          distance_2: Math.round(100 * training.distance_2) / 100,
          distance_3: Math.round(100 * training.distance_3) / 100,
          distance_4: Math.round(100 * training.distance_4) / 100,
          distance_5: Math.round(100 * training.distance_5) / 100,
          distance_total: round(training.distance),
        };
      } else if (wellness === true && key !== "hrs_sleep") {
        let desc_key = key + "_desc";
        if (key === "sleep_quality") {
          desc_key = "sleep_desc";
        } else if (key === "muscle_soreness") {
          desc_key = "sore_desc";
        }

        return {
          date: training.date.slice(5),
          name: training.name,
          player_id: training.player_id,
          disp_name:
            training.name !== undefined
              ? training.name.split(" ")[training.name.split(" ").length - 1]
              : undefined,
          [key]: Math.round(training[key]),
          [desc_key]: training[desc_key],
        };
      } else {
        let value = training[key];

        return {
          date: training.date.slice(5),
          name: training.name,
          player_id: training.player_id,
          disp_name:
            training.name !== undefined
              ? training.name.split(" ")[training.name.split(" ").length - 1]
              : undefined,
          [key]: value,
        };
      }
    });
  }


  let charts = {};



  const maxHrData = useMemo(() => {
    const maxHrStackKeys = Object.keys(data).filter((key) =>
      key.startsWith("max_hr_perc")
    );
    return data["max_hr"]
      ?.map((item) =>
        maxHrStackKeys.reduce(
          (acc, curr) => {
            acc[curr] = data[curr].find(
              ({ player_id }) => player_id === item.player_id
            )[curr];
            return acc;
          },
          { ...item, disp_date: item.name }
        )
      )
      .sort((a, b) => {
        const sumA = maxHrStackKeys.reduce((acc, curr) => acc + a[curr], 0);
        const sumB = maxHrStackKeys.reduce((acc, curr) => acc + b[curr], 0);
        return sumB - sumA;
      });
  }, [data]);

  const getMappingFunction = (metric) => {

    let customChart = null;
    let chartType = null;
    if(metric?.customChartData) {
      customChart = metric?.customChartData
      chartType = metric?.customChartData?.type
    }

    const hiddenMetrics = data?.metrics.filter((e) => e.visible === false).map((e) => e.key)
  
    switch (chartType) {
			case 'bar_and_bubble_chart': {
        if (hiddenMetrics.includes(metric?.customChartData?.bar_metric) && hiddenMetrics.includes(metric?.customChartData?.bubble_metric)){
          return undefined
        }
				return mapDataForBarAndBubbleChart({
          dataIn: data,
          mainSeriesMetric: data.metrics.find( (metric) => metric.key === customChart.bar_metric),
          secondarySeriesMetric: data.metrics.find( (metric) => metric.key === customChart.bubble_metric),
          isTeamChart: true,
          singleAxis: true,
          teamName,
          games,
          teamColors: team_colors.all,
          showAvgPlotline: true,
          isBreakdown: true,
          acwrThresholdConfig,
          customTitle: customChart?.title,
          dataColorUtil
				});
			}
      case 'stacked_bar_chart': {
        if (hiddenMetrics.includes(metric?.customChartData?.bar1_metric) && hiddenMetrics.includes(metric?.customChartData?.bar2_metric)){
          return undefined
        }
        const stackedBarData = getStackedBarData(customChart?.stack_type);
				return stackedBarChartDtoToModel({
				  data: stackedBarData,
				  chartType: customChart?.stack_type,
				  games,
				  teamName,
				  showACWR: false,
          isTeamChart: true,
          isBreakdownChart: true,
				  customChartConfig: customChart,
          metrics: data?.metrics,
          acwrThresholdConfig
				});
			}
      case 'grouped_bar_chart': {
        if (hiddenMetrics.includes(metric?.customChartData?.bar1_metric) && hiddenMetrics.includes(metric?.customChartData?.bar2_metric)){
          return undefined
        }
        return mapDataForHighchartsGroupedBar({
          dataIn: data,
          games,
          metrics: data.metrics.filter((m) => m.key === customChart.bar1_metric || m.key === customChart.bar2_metric),
          teamColors: team_colors.all,
          isComparison: false,
          isTeamChart: true,
          isMetricComparisonChart: true,
          title: customChart?.title,
          dataColorUtil
        });
      }
			default: {
				// custom chart type either not provided or not recognized.
				// Will get handled by the code below
			}
		}

    if(MAX_VELO_METRICS.concat(TOTAL_DISTANCE_METRICS).includes(metric.key)) {
      return mapDataForMaxVeloChart({dataIn: data, metric, teamName, games, teamColors: team_colors.all, isTeamChart: true,
        acwrThresholdConfig, dataColorUtil
      });
    }
     
    return mapDataForHighcharts({dataIn: data, metric, teamName, games, teamColors: team_colors.all, isTeamChart: true, 
      showAvgPlotline: true, singleAxis: true, teamMetric: teamMetric, acwrThresholdConfig,
      dataColorUtil
    });
  }

  const getStackedBarData = (stackType) => {
    switch (stackType) {
      case "distance":
        return data?.data;
      case "maxHrStacks":
        return maxHrData;
      case "hrZone":
        return hrZoneData;
      default:
        return data?.data;
    }
  }



  const hrZoneData = useMemo(() => {
    const hrZoneStackKeys = Object.keys(data).filter((key) =>
      key.startsWith("hr_zone_")
    );
    return data["hr_zone_1"]
      ?.map((item) =>
      hrZoneStackKeys.reduce(
          (acc, curr) => {
            acc[curr] = data[curr].find(
              ({ player_id }) => player_id === item.player_id
            )[curr];
            return acc;
          },
          { ...item, disp_date: item.name }
        )
      )
      .sort((a, b) => {
        const sumA = hrZoneStackKeys.reduce((acc, curr) => acc + a[curr], 0);
        const sumB = hrZoneStackKeys.reduce((acc, curr) => acc + b[curr], 0);
        return sumB - sumA;
      });
  }, [data]);

  const metricsToRender = getMetricsToRender(teamName, data?.metrics, customChartData?.chart_types);

  return (
    <>
        <div className="outer-example-grid" style={{display: "flex", justifyContent: "center"}}>
          <ul className="example-breakdown-table-grid" style={{width: '90%', display: "flex", justifyContent: "center"}}>

            {
              (data?.metrics?.length && customChartData?.chart_types && metricsToRender?.length) ? (
								metricsToRender
								.map( (metric, index) => {
                  const options = getMappingFunction(metric)

                  if (!options) {return <></>}

                  return (
                    <li key={index} className="example-table-graph example-breakdown-grid exportable breakdown-chart width-100">
                      <BarChartHighcharts options={options} height={'inherit'}>
                      </BarChartHighcharts>
                    </li>  
                  )
                })
					  ) : <></>}

            {/* TODO: replace this wellness chart */}

            {data["wellness_metrics"].map((metric, index) => {
              return (
                <li
                  key={metric.key}
                  className="example-table-graph example-breakdown-grid"
                >
                  {charts[metric.key]}
                </li>
              );
            })}
          </ul>
        </div>
    </>
  )
}
